import type { ZodType } from "zod";
import z from "zod";

import {
  OrganisationIdSchema,
  OrganisationSchema,
  type OrganisationIdUnbranded,
  type OrganisationUnbranded,
} from "./organisation.js";
import { CustomerRoleSchema, type CustomerRole } from "./thalamos.js";
import {
  EmailAddressSchema,
  UserIdSchema,
  UserNameSchema,
  type EmailAddressUnbranded,
  type UserIdUnbranded,
} from "./user.js";

/**
 * The longest an address can be: a 64-character local part, `@`, and a
 * 255-character domain.
 */
const EMAIL_MAX_LENGTH = 320;

/**
 * An address as somebody may type one for a new account.
 *
 * Trimmed and lower-cased before it is checked, so the account is stored under
 * the form every lookup compares against, and two administrators typing one
 * address in different cases reach the same account.
 *
 * The messages are translation keys, for the reason `UserNameSchema` gives.
 */
export const NewAccountEmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(EMAIL_MAX_LENGTH, "admin.users.create.email.tooLong")
  .pipe(z.email("admin.users.create.email.invalid"))
  .pipe(EmailAddressSchema) satisfies ZodType<EmailAddressUnbranded>;

/**
 * An account to create, and the membership it starts with.
 *
 * The membership is part of the request because an account holding nothing is
 * of no use to anybody, and creating it and granting the membership separately
 * would leave a window where it exists and belongs nowhere.
 *
 * `addToExistingAccount` is the answer to being told the address already has an
 * account: grant the membership to that one instead. Required to be said
 * outright, so a typing slip in an address cannot silently grant a stranger a
 * membership.
 */
export type CreateUserRequestUnbranded = {
  name: string;
  email: EmailAddressUnbranded;
  organisationId: OrganisationIdUnbranded;
  role: CustomerRole;
  addToExistingAccount?: boolean | undefined;
};

export const CreateUserRequestSchema = z
  .object({
    name: UserNameSchema,
    email: NewAccountEmailSchema,
    organisationId: OrganisationIdSchema,
    role: CustomerRoleSchema,
    addToExistingAccount: z.boolean().optional(),
  })
  .brand<"haven-common:CreateUserRequest">() satisfies ZodType<CreateUserRequestUnbranded>;

export type CreateUserRequest = z.infer<typeof CreateUserRequestSchema>;

/**
 * The account a creation reached: the new one, or the existing one the
 * membership was granted to.
 */
export type CreatedUserUnbranded = {
  id: UserIdUnbranded;
};

export const CreatedUserSchema = z.object({
  id: UserIdSchema,
}) satisfies ZodType<CreatedUserUnbranded>;

export type CreatedUser = z.infer<typeof CreatedUserSchema>;

/**
 * The organisations a session may give somebody a membership at, named, for a
 * picker. Wrapped in an object for the reason `UserMemberships` gives.
 */
export type ProvisionableOrganisationsUnbranded = {
  organisations: OrganisationUnbranded[];
};

export const ProvisionableOrganisationsSchema = z.object({
  organisations: z.array(OrganisationSchema),
}) satisfies ZodType<ProvisionableOrganisationsUnbranded>;

export type ProvisionableOrganisations = z.infer<
  typeof ProvisionableOrganisationsSchema
>;
